import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { SiteWordmark } from '@/components/branding/SiteWordmark'
import { ForumSeo } from '@/components/ForumSeo'
import { Button } from '@/components/ui/button'
import { apiFetch } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  forumAlertError,
  forumCard,
  forumCardHeader,
  forumField,
  forumKicker,
  forumLink,
  forumMutedSm,
  forumTitleLg,
} from '@/lib/forumUi'
import { forumPageTitle, truncateForMetaDescription } from '~shared/seo'

export function SignInPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = params.get('next') ?? '/'

  const { reload } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="nk-container flex flex-1 flex-col py-10 md:items-center md:justify-center md:py-14">
      <ForumSeo
        title={forumPageTitle('Sign in')}
        description={truncateForMetaDescription(
          'Secure sign-in for NK Our Town, the North Kingstown RI community forum.',
        )}
        noindex
      />
      <div className="mx-auto mb-6 flex w-full max-w-xl justify-center md:mb-8">
        <Link to="/" className="rounded-sm no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-100">
          <SiteWordmark className="h-9 sm:h-10" />
          <span className="sr-only">NK Our Town — home</span>
        </Link>
      </div>
      <main className={`mx-auto w-full max-w-xl ${forumCard}`}>
        <header className={forumCardHeader}>
          <p className={forumKicker}>Account</p>
          <h1 className={forumTitleLg}>Sign in</h1>
          <p className={`${forumMutedSm} mt-2 text-zinc-600`}>
            Use your registered email and password. Session cookies are httpOnly on the worker.
          </p>
        </header>

        <div className="space-y-4 px-5 py-5 md:px-6 md:py-6">
          {error ? <div role="alert" className={forumAlertError}>{error}</div> : null}

          <form
            className="space-y-4"
            aria-label="Sign in"
            onSubmit={async evt => {
              evt.preventDefault()
              try {
                setError(null)
                await apiFetch(`/api/auth/sign-in`, {
                  method: 'POST',
                  json: { email, password },
                })
                await reload()
                navigate(next)
              } catch {
                setError('Email or password did not match.')
              }
            }}
          >
            <div>
              <label className="mb-2 block text-[13px] font-semibold text-zinc-800" htmlFor="signin-email">
                Email
              </label>
              <input
                id="signin-email"
                value={email}
                onChange={evt => setEmail(evt.target.value)}
                autoComplete="email"
                className={forumField}
                type="email"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-[13px] font-semibold text-zinc-800" htmlFor="signin-password">
                Password
              </label>
              <input
                id="signin-password"
                value={password}
                onChange={evt => setPassword(evt.target.value)}
                autoComplete="current-password"
                className={forumField}
                type="password"
                required
              />
            </div>

            <div className="pt-2">
              <Button type="submit" className="w-full md:w-auto">
                Sign in
              </Button>
            </div>
          </form>

          <p className="border-t border-zinc-100 pt-4 text-[14px] text-zinc-600">
            Need an account?{' '}
            <Link className={forumLink} to={`/sign-up?next=${encodeURIComponent(next)}`}>
              Register
            </Link>
          </p>
        </div>
      </main>
    </div>
  )
}
